import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import logger from '../utils/logger';
import { API_ENDPOINTS, STORAGE_KEYS, ROUTES } from '../utils/constants';

export const AuthContext = createContext();

// Auto-logout after 30 minutes (1800000 ms) of inactivity
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    // Clear inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Only set timer if user is logged in
    if (user) {
      // Calculate time remaining
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      const timeRemaining = Math.max(0, INACTIVITY_TIMEOUT - timeSinceLastActivity);

      // Set new timer
      inactivityTimerRef.current = setTimeout(() => {
        logger.info('Auto-logout due to inactivity');
        signOut();
        // Redirect to signin page
        window.location.href = ROUTES.SIGNIN;
      }, timeRemaining);
    }
  }, [user, signOut]);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await api.get(API_ENDPOINTS.USER.PROFILE);
      setUser(response.data);
      lastActivityRef.current = Date.now();
    } catch (error) {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up activity listeners when user is logged in
  useEffect(() => {
    if (!user) {
      // Clear timer if user logs out
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      return;
    }

    // Throttle activity updates to reduce overhead (update max once per second)
    let lastUpdate = 0;
    const throttledUpdateActivity = () => {
      const now = Date.now();
      if (now - lastUpdate >= 1000) {
        lastUpdate = now;
        updateActivity();
      }
    };

    // Use fewer, more efficient event listeners
    // Removed 'mousemove' as it fires too frequently
    const events = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, throttledUpdateActivity, true);
    });

    // Initialize timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledUpdateActivity, true);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [user, updateActivity, resetInactivityTimer]);

  const signIn = async (email, password) => {
    try {
      setLoading(true); // Set loading to true while fetching profile
      const response = await api.post(API_ENDPOINTS.AUTH.SIGNIN, { email, password });
      
      // Check if 2FA is required
      if (response.data.requiresTwoFactor) {
        setLoading(false);
        // Return the response with 2FA flag and temp token
        return {
          requiresTwoFactor: true,
          tempToken: response.data.tempToken,
          message: response.data.message
        };
      }
      
      const { token } = response.data;
      if (!token) {
        throw new Error('No token received from server');
      }
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Fetch full user profile to get bank details and other information
      await fetchUserProfile();
      lastActivityRef.current = Date.now(); // Reset activity time
      return response.data;
    } catch (error) {
      setLoading(false); // Ensure loading is false on error
      logger.errorWithStack('Sign in error:', error);
      throw error;
    }
  };

  const verifyTwoFactor = async (code, tempToken) => {
    try {
      setLoading(true);
      const response = await api.post(API_ENDPOINTS.AUTH.TWO_FACTOR.VERIFY, { code, tempToken });
      const { token } = response.data;
      if (!token) {
        throw new Error('No token received from server');
      }
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Fetch full user profile to get bank details and other information
      await fetchUserProfile();
      lastActivityRef.current = Date.now(); // Reset activity time
      return response.data;
    } catch (error) {
      setLoading(false);
      logger.errorWithStack('2FA verification error:', error);
      throw error;
    }
  };

  const signUp = async (userData) => {
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.SIGNUP, userData);
      // Signup now returns success message instead of token (user must verify email first)
      // Return the response data which includes the message
      return response.data;
    } catch (error) {
      logger.errorWithStack('Sign up error:', error);
      throw error;
    }
  };


  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, fetchUserProfile, verifyTwoFactor }}>
      {children}
    </AuthContext.Provider>
  );
};
